import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { setPreferredLanguage, getPreferredLanguage } from '../utils/bilingualResolver';
import { translateHtmlString } from '../utils/translation';
import { useAuth } from '../context/AuthContext';
import LanguageToggle from '../components/LanguageToggle';
import styles from './CheatsheetViewer.module.css';

function getDevanagariRatio(text) {
    if (typeof text !== 'string' || !text.length) return 0;
    const devanagariCount = (text.match(/[\u0900-\u097F]/g) || []).length;
    const alphaCount = (text.match(/[A-Za-z\u0900-\u097F]/g) || []).length;
    if (!alphaCount) return 0;
    return devanagariCount / alphaCount;
}

function normalizeCheatsheetHtml(html) {
    if (typeof html !== 'string' || !html.length) return html;

    let normalized = html
        .replace(/katex\.न्यूनतम\.css/g, 'katex.min.css')
        .replace(/katex\.न्यूनतम\.js/g, 'katex.min.js')
        .replace(/auto-render\.न्यूनतम\.js/g, 'auto-render.min.js');

    const canonicalAutoRenderScript =
        '<script defer src="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js" onload="renderMathInElement(document.body,{delimiters:[{left:\'$$\',right:\'$$\',display:true},{left:\'$\',right:\'$\',display:false}]});"></script>';

    // Some generated Hindi files contain a broken auto-render script tag that swallows
    // following markup. Replace any auto-render script block up to <style> with a safe script.
    normalized = normalized.replace(
        /<script[^>]*auto-render\.min\.js"[\s\S]*?<style>/i,
        () => `${canonicalAutoRenderScript}\n<style>`
    );

    return normalized;
}

function getComparableHtmlTextLength(html) {
    if (typeof html !== 'string' || !html.trim()) return 0;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const clone = doc.body ? doc.body.cloneNode(true) : null;
        if (!clone) return 0;
        clone.querySelectorAll('script,style,noscript,svg,math,iframe').forEach((n) => n.remove());
        return (clone.textContent || '').replace(/\s+/g, ' ').trim().length;
    } catch {
        return 0;
    }
}

export default function CheatsheetViewer() {
    const { filename } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const inferredReturnSection = /handbook/i.test(filename || '') ? 'handbooks' : 'cheatsheets';
    const returnSection = location.state?.returnSection || inferredReturnSection;
    const returnTab = location.state?.returnTab || (returnSection === 'handbooks' ? 'handbooks' : 'sheets');
    const returnHash =
        location.state?.returnHash ||
        (returnSection === 'handbooks' ? '#master-handbooks' : '#cheatsheets');
    const iframeRef = useRef(null);
    const iframeMutationObserverRef = useRef(null);
    const iframeResizeCleanupRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(600);
    const [blobUrl, setBlobUrl] = useState(null);
    const [loadError, setLoadError] = useState(false);
    // Initialise from the saved account/browser preference — never hardcode
    // 'english' here, that used to wipe the user's saved Hindi choice on mount.
    const [language, setLanguage] = useState(getPreferredLanguage);
    const [hindiFallbackToEnglish, setHindiFallbackToEnglish] = useState(false);
    const [hindiAutoGenerated, setHindiAutoGenerated] = useState(false);
    const { currentUser } = useAuth();

    // Persist to localStorage + the account's Firestore doc (cross-device sync)
    useEffect(() => {
        setPreferredLanguage(language, currentUser?.uid);
    }, [language, currentUser?.uid]);

    // If the account-level preference arrives from Firestore after mount,
    // follow it (another device may have changed it).
    useEffect(() => {
        const onLangSync = (e) => { if (e.detail) setLanguage(e.detail); };
        window.addEventListener('ppx-lang', onLangSync);
        return () => window.removeEventListener('ppx-lang', onLangSync);
    }, []);

        const injectHandbookMobileOverrides = (html) => {
                if (!filename || !/handbook/i.test(filename)) return html;

                const mobileOverrideStyle = `
<style id="ppx-mobile-handbook-override">
/* Shared handbook reader controls */
#ppx-handbook-controller {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 12px 14px;
    margin: 0 0 14px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

#ppx-handbook-controller .ppx-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

#ppx-handbook-controller label {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #64748b;
}

#ppx-handbook-controller select {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
    color: #111827;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    padding: 9px 12px;
    box-shadow: inset 0 1px 1px rgba(15, 23, 42, 0.02);
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

#ppx-handbook-controller select:hover {
    border-color: #94a3b8;
}

#ppx-handbook-controller select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.16);
}

#ppx-handbook-controller select:disabled {
    color: #9ca3af;
    background: #f9fafb;
}

/* Hide chapter index only after controller successfully initializes. */
.ppx-controller-ready .toc-card {
    display: none !important;
}

.ppx-controller-ready section.chapter {
    display: none !important;
}

.ppx-controller-ready section.chapter.ppx-active {
    display: block !important;
}

@media (max-width: 768px) {
    html, body {
        margin: 0 !important;
        padding: 0 !important;
        max-width: 100% !important;
        overflow-x: hidden !important;
        background: #ffffff !important;
    }

    body > * {
        max-width: 100% !important;
    }

    [class*="container"],
    [class*="wrapper"],
    [class*="page"],
    [class*="layout"],
    main,
    section {
        max-width: 100% !important;
        width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
    }

    /* Handbook templates use these exact classes with large side padding. */
    .wrap,
    .container,
    main.container {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .hero {
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    /* Keep header text readable: slight inset only for hero content. */
    .hero .wrap {
        padding-left: 12px !important;
        padding-right: 12px !important;
    }

    .toc-card,
    .chapter {
        margin-left: 0 !important;
        margin-right: 0 !important;
    }

    #ppx-handbook-controller {
        grid-template-columns: 1fr;
        border-right: none;
        border-radius: 10px;
        margin-bottom: 10px;
        padding: 12px;
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
    }
}
</style>`;

                const controllerScript = `
<script id="ppx-handbook-controller-script">
(function () {
    function textFrom(el) {
        return (el && el.textContent ? el.textContent : '').replace(/\\s+/g, ' ').trim();
    }

    function notifyHeight() {
        var h = Math.max(
            document.documentElement ? document.documentElement.scrollHeight : 0,
            document.body ? document.body.scrollHeight : 0
        );
        if (window.parent) {
            window.parent.postMessage({ type: 'ppx-handbook-height', height: h }, '*');
        }
    }

    function renderMathSafely() {
        if (typeof window.renderMathInElement !== 'function' || !document.body) return;
        try {
            window.renderMathInElement(document.body, {
                delimiters: [
                    { left: '$$', right: '$$', display: true },
                    { left: '$', right: '$', display: false },
                ],
                throwOnError: false,
            });
        } catch (_) {
            // Keep handbook usable even if some math token is malformed.
        }
    }

    function buildController() {
        var chapters = Array.prototype.slice.call(document.querySelectorAll('section.chapter'));
        if (!chapters.length) return false;

        var existing = document.getElementById('ppx-handbook-controller');
        if (existing) existing.remove();

        var chapterSelect = document.createElement('select');
        chapterSelect.id = 'ppx-chapter-select';

        chapters.forEach(function (chapter, index) {
            if (!chapter.id) chapter.id = 'ppx-auto-chapter-' + (index + 1);
            var title = textFrom(chapter.querySelector('.ch-title')) || ('Chapter ' + (index + 1));
            var option = document.createElement('option');
            option.value = String(index);
            option.textContent = (index + 1) + '. ' + title;
            chapterSelect.appendChild(option);
        });

        var controller = document.createElement('div');
        controller.id = 'ppx-handbook-controller';
        controller.innerHTML =
            '<div class="ppx-field"><label for="ppx-chapter-select">Select Chapter To Read</label></div>';

        controller.children[0].appendChild(chapterSelect);

        var firstChapter = chapters[0];
        firstChapter.parentNode.insertBefore(controller, firstChapter);

        function activateChapter(chapterIndex) {
            chapters.forEach(function (chapter, index) {
                chapter.classList.toggle('ppx-active', index === chapterIndex);
            });
            renderMathSafely();
            requestAnimationFrame(notifyHeight);
            setTimeout(notifyHeight, 60);
        }

        document.body.classList.add('ppx-controller-ready');

        chapterSelect.addEventListener('change', function () {
            activateChapter(Number(chapterSelect.value || 0));
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });

        activateChapter(0);
        return true;
    }

    function boot() {
        buildController();
        renderMathSafely();
        notifyHeight();
    }

    window.addEventListener('DOMContentLoaded', function () {
        boot();
        setTimeout(boot, 120);
        setTimeout(boot, 600);
    });
    window.addEventListener('load', function () {
        boot();
        setTimeout(boot, 300);
    });

    var observer = new MutationObserver(function () {
        if (!document.getElementById('ppx-handbook-controller') && document.querySelector('section.chapter')) {
            boot();
        }
        notifyHeight();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
})();
</script>`;

                if (html.includes('</head>')) {
                        return html.replace('</head>', `${mobileOverrideStyle}${controllerScript}</head>`);
                }

                return `${mobileOverrideStyle}${controllerScript}${html}`;
        };

    // Fetch the HTML and create a blob URL to bypass X-Frame-Options completely
    useEffect(() => {
        if (!filename) return;
        let cancelled = false;
        let objectUrl;
        setLoadError(false);
        setBlobUrl(null);
        setHindiFallbackToEnglish(false);
        setHindiAutoGenerated(false);

        const candidates = language === 'hindi'
            ? [
                `/cheatsheets/${filename}.hi.html`,
                `/cheatsheets/${filename}_hi.html`,
                `/cheatsheets/${filename}.html`,
            ]
            : [`/cheatsheets/${filename}.html`];

        (async () => {
            try {
                let html = null;
                let selectedPath = '';
                let didAutoGenerateHindi = false;

                for (const path of candidates) {
                    const resp = await fetch(path);
                    if (resp.ok) {
                        html = await resp.text();
                        selectedPath = path;
                        break;
                    }
                }

                if (!html) throw new Error('Not found');

                const isHindiRequest = language === 'hindi';
                const isScaffoldHindi =
                    isHindiRequest &&
                    selectedPath.endsWith('.hi.html') &&
                    /TODO:\s*Replace English content with verified Hindi content/i.test(html);

                const isLowHindiQuality =
                    isHindiRequest &&
                    (selectedPath.endsWith('.hi.html') || selectedPath.endsWith('_hi.html')) &&
                    getDevanagariRatio(html) < 0.12;

                let englishHtml = null;
                let isShortHindiCoverage = false;
                if (isHindiRequest) {
                    const englishResp = await fetch(`/cheatsheets/${filename}.html`);
                    if (englishResp.ok) {
                        englishHtml = await englishResp.text();
                        if (selectedPath.endsWith('.hi.html') || selectedPath.endsWith('_hi.html')) {
                            const hiLen = getComparableHtmlTextLength(html);
                            const enLen = getComparableHtmlTextLength(englishHtml);
                            isShortHindiCoverage = enLen > 1500 && hiLen / enLen < 0.65;
                        }
                    }
                }

                if (isHindiRequest && (selectedPath.endsWith(`${filename}.html`) || isScaffoldHindi || isLowHindiQuality || isShortHindiCoverage)) {
                    if (englishHtml) {
                        html = await translateHtmlString(englishHtml, 'hindi');
                        didAutoGenerateHindi = true;
                        setHindiAutoGenerated(true);
                    }
                }

                const processedHtml = injectHandbookMobileOverrides(normalizeCheatsheetHtml(html));
                const blob = new Blob([processedHtml], { type: 'text/html' });
                objectUrl = URL.createObjectURL(blob);

                if (!cancelled) {
                    setBlobUrl(objectUrl);
                    if (language === 'hindi' && selectedPath.endsWith(`${filename}.html`) && !didAutoGenerateHindi) {
                        setHindiFallbackToEnglish(true);
                    }
                }
            } catch {
                if (!cancelled) setLoadError(true);
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [filename, language]);

    // Derive a clean title from filename
    const title = filename
        ? filename.replace(/^[A-Za-z]+_\d+_/, '').replace(/_/g, ' ')
        : 'Cheatsheet';

    // Category prefix for display  e.g. "Chem 01"
    const numPrefix = filename
        ? filename.replace(/_/g, ' ').split(' ').slice(0, 2).join(' ')
        : '';

    // After iframe loads, read its actual content height
    const handleLoad = () => {
        try {
            const doc = iframeRef.current?.contentDocument;
            const updateHeight = () => {
                const h = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight;
                if (h && h > 200) setIframeHeight(h);
            };

            updateHeight();

            if (iframeMutationObserverRef.current) {
                iframeMutationObserverRef.current.disconnect();
            }
            if (iframeResizeCleanupRef.current) {
                iframeResizeCleanupRef.current();
                iframeResizeCleanupRef.current = null;
            }

            const observer = new MutationObserver(() => {
                window.requestAnimationFrame(updateHeight);
            });

            if (doc?.documentElement) {
                observer.observe(doc.documentElement, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                    characterData: true,
                });
                iframeMutationObserverRef.current = observer;
            }

            const win = iframeRef.current?.contentWindow;
            if (win) {
                const onResize = () => window.requestAnimationFrame(updateHeight);
                win.addEventListener('resize', onResize);
                iframeResizeCleanupRef.current = () => win.removeEventListener('resize', onResize);
            }
        } catch {
            // cross-origin fallback
        }
    };

    useEffect(() => {
        const onMessage = (event) => {
            if (!iframeRef.current?.contentWindow || event.source !== iframeRef.current.contentWindow) {
                return;
            }

            if (event.data?.type === 'ppx-handbook-height' && typeof event.data.height === 'number') {
                if (event.data.height > 200) setIframeHeight(event.data.height);
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    useEffect(() => {
        return () => {
            if (iframeMutationObserverRef.current) {
                iframeMutationObserverRef.current.disconnect();
            }
            if (iframeResizeCleanupRef.current) {
                iframeResizeCleanupRef.current();
            }
        };
    }, []);

    return (
        <div className={styles.page}>
            {/* ── Toolbar ── */}
            <div className={styles.toolbar}>
                <div className={styles.toolbarInner}>
                    <button
                        className={styles.backBtn}
                        onClick={() => navigate(`/study-materials${returnHash}`, { state: { returnSection, returnTab } })}
                    >
                        ← Study Materials
                    </button>

                    <div className={styles.titleBlock}>
                        <span className={styles.numPill}>{numPrefix}</span>
                        <span className={styles.sheetTitle}>{title}</span>
                    </div>
                    <LanguageToggle language={language} onChange={setLanguage} />
                </div>
            </div>

            {/* ── Viewer ── */}
            <div className={styles.viewerArea}>
                {hindiAutoGenerated && (
                    <div className={styles.loadingState}>
                        Hindi content has been generated for this handbook/sheet and is ready to read.
                    </div>
                )}
                {hindiFallbackToEnglish && (
                    <div className={styles.loadingState}>
                        Hindi version is not added yet for this sheet. Showing English version.
                    </div>
                )}
                {loadError ? (
                    <div className={styles.errorState}>
                        <p>Unable to load this sheet. Please try downloading it instead.</p>
                    </div>
                ) : !blobUrl ? (
                    <div className={styles.loadingState}>Loading…</div>
                ) : (
                    <iframe
                        ref={iframeRef}
                        src={blobUrl}
                        title={title}
                        className={styles.iframe}
                        style={{ height: iframeHeight }}
                        onLoad={handleLoad}
                    />
                )}
            </div>
        </div>
    );
}
